import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mui } from '~/tools/mui';
import { icons } from '~/tools/icons';
import { jss } from '~/tools/jss';
import { validate } from '~/tools/validate';
import { uiSelectors } from '~/behaviors/selectors/ui.selectors';
import { accountActions } from '~/behaviors/actions/account.actions';
import { useTranslation, useAppDispatch, useAppSelector } from '~/behaviors';
import Button from '~/components/Button';
import { Dialogs, Alert } from '../UI.shared';

const useStyles = jss.makeStyles((theme) => ({
  cardContent: {
    padding: 24,
    width: 476,
  },
  instructionText: {
    fontSize: 18,
    textAlign: 'center',
  },
  form: {
    textAlign: 'center',
    minHeight: 100,
    marginTop: 24,
  },
  inputLabel: {
    fontSize: 14,
  },
  input: {
    fontSize: 14,
    height: 40,
    boxSizing: 'border-box',
    padding: '8px 16px',
  },
  submitBtnContainer: {
    margin: '24px 0px 0px 0px',
  },
  errText: {
    paddingTop: theme.spacing(2),
  },
  navLinkContainer: {
    marginTop: 24,
  },
  alert: {
    marginBottom: '1rem',
  },
}));

const defaultDialogState = {
  title: '',
  message: '',
  isVisible: false,
  closeButtonLabel: '',
};

const ForgotPasswordPage: React.FC = () => {
  // ------------------------------------------------------------------ State --
  const styles = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { forgotPasswordEmail, isLockedOut } = useAppSelector(uiSelectors.selectAccountUI());

  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [dialog, setDialog] = useState(defaultDialogState);

  // -------------------------------------------------------------- Lifecycle --
  useEffect(() => {
    if (!email && forgotPasswordEmail) {
      setEmail(forgotPasswordEmail);
    }
  }, [forgotPasswordEmail, email]);

  // ----------------------------------------------------------------- Events --
  const handleDialogClose = () => {
    navigate('/login');
    setDialog(defaultDialogState);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (emailError) return;

    // @ts-ignore @TODO Epic WT-21600
    dispatch(accountActions.forgotPassword(email)).then((action) => {
      if (action.error) return setError(action.error.message);

      setDialog({
        title: t('forgot.password.dialog.title'),
        message: t('forgot.password.dialog.description'),
        isVisible: true,
        closeButtonLabel: t('back.to.login'),
      });
    });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;

    setEmail(newEmail);

    if (validate.isEmail(newEmail)) {
      setEmailError('');
    } else {
      setEmailError('invalid.email.format');
    }
  };

  // -------------------------------------------------------------- Interface --
  return (
    <mui.CardContent className={styles.cardContent} data-testid="forgot-password-page">
      <Dialogs.Simple
        title={dialog.title}
        message={dialog.message}
        isVisible={dialog.isVisible}
        closeButtonLabel={dialog.closeButtonLabel}
        onClose={handleDialogClose}
        Icon={icons.Email}
        SmallIcon={icons.Send}
      />

      {isLockedOut && (
        // @ts-ignore @TODO Epic WT-21600
        <Alert title={t('title.lockedOut')} severity="error" className={styles.alert}>
          {t('error.lockedOut')}
        </Alert>
      )}

      <mui.Typography className={styles.instructionText}>{t('forgot.password.instruction')}</mui.Typography>

      <form className={styles.form} onSubmit={handleFormSubmit}>
        <mui.TextField
          id="email"
          name="email"
          size="small"
          autoComplete="email"
          aria-label="email"
          // @ts-ignore @TODO Epic WT-21600
          className={styles.inputContainer}
          required
          fullWidth
          autoFocus
          variant="outlined"
          value={email}
          label={t('account.email')}
          onChange={handleEmailChange}
          inputProps={{ 'data-testid': 'email' }}
          InputProps={{ classes: { input: styles.input } }}
          InputLabelProps={{ classes: { root: styles.inputLabel } }}
          error={!!emailError}
          helperText={t(emailError)}
        />

        {error && (
          <mui.FormHelperText error id="error-text" data-testid="error">
            {error}
          </mui.FormHelperText>
        )}

        <div className={styles.submitBtnContainer}>
          <Button
            aria-label={t('button.launchNow')}
            fullWidth
            color="primary"
            variant="contained"
            type="submit"
            disabled={email === '' || !!emailError}
            // @ts-ignore @TODO Epic WT-21600
            className={styles.submitBtn}
            data-testid="submit"
          >
            {t('send.password.reset.email')}
          </Button>
        </div>

        <div className={styles.navLinkContainer}>
          <Link to="/login">{t('back.to.login')}</Link>
        </div>
      </form>
    </mui.CardContent>
  );
};

export default ForgotPasswordPage;
